import { YouTube as YouTubeIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'src/components/hook-form';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Button, Typography, Stack, Card, TextField, IconButton, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CommonHelper } from 'src/utils/commonHelper';

const WhiteLabel = () => {
  const { t: translate } = useTranslation();
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [formSchema, setFormSchema] = useState({});
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");
  const [domainUrl, setDomainUrl] = useState('');
  const [cNameKey, setCNameKey] = useState('');
  const [isDomainVerified, setIsDomainVerified] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [domainDetails, setDomainDetails] = useState(null);
  const [isNextLoading, setNextLoading] = useState(false);
  const [isVerifyLoading, setVerifyLoading] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [showHelperText, setShowHelperText] = useState(true);
  const [copiedTxt, setCopiedTxt] = useState(false);
  const [copiedCname, setCopiedCname] = useState(false);
  const [copiedTxtValue, setCopiedTxtValue] = useState(false);
  const [copiedCnameValue, setCopiedCnameValue] = useState(false);
  const { showToast } = useToaster();

  useEffect(() => {
    generateFormModelSchema();
    getBrandingDetails();
  }, []);

  const generateFormModelSchema = () => {
    let fields = [
      ValidationTool.GenerateSchema('domainUrl', translate('manage_branding.white_label.domain_url'), 'TEXT', true),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const getBrandingDetails = () => {
    let postProps = {
      url: userModuleURL.getBrandingDetails,
      successCallback: domainSuccessCallback,
      failureCallback,
    };
    HttpServices.Get(postProps);
  };

  const domainSuccessCallback = (data, message) => {
    setDomainDetails(data);
    setCNameKey(data.cname_key);
    setDomainUrl(data.domain_name);
    setIsDomainVerified(data.is_verified);
    if (data.domain_name !== '') {
      setShowDelete(true);
    }
    setNextLoading(false);
  };

  const failureCallback = (message) => {
    setNextLoading(false);
    setVerifyLoading(false);
    message && showToast(message, 'e');
  };

  const handleInputChange = (event) => {
    setDomainUrl(event.target.value);
  };

  const defaultValues = {
    domainUrl: domainUrl || '',
  };

  const methods = useForm({
    defaultValues,
  });

  const { handleSubmit } = methods;

  const handleSubmitForm = () => {
    let userForm = {
      domainUrl,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.DomainValidation(formSchema, userForm);
    if (isValidForm) {
      setFormValidationErrors({});
      onSubmit();
    } else {
      setFormValidationErrors(formValidationErrors);
      setShowHelperText(false);
    }
  };

  const cNameSuccessCallback = (data, message) => {
    setNextLoading(false);
    setCNameKey(data.cname_key);
    setShowDelete(true);
  };

  const onSubmit = () => {
    setNextLoading(true);
    let domainProps = {
      domain_name: domainUrl,
    };
    let postProps = {
      url: userModuleURL.addCName,
      body: domainProps,
      successCallback: cNameSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const verifyCNameSuccessCallback = (data, message) => {
    data && getBrandingDetails();
    setVerifyLoading(false);
  };

  const handleVerify = () => {
    setVerifyLoading(true);
    let verifyProps = {
      domain_name: domainUrl,
    };
    let postProps = {
      url: userModuleURL.verifyCName,
      body: verifyProps,
      successCallback: verifyCNameSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const deleteCNameSuccessCallback = () => {
    getBrandingDetails();
    setShowDelete(false);
    setLoading(false);
  };

  const handleDeleteDomain = () => {
    setLoading(true);
    let deleteProps = {
      org_id: domainDetails.org_id,
    };
    let postProps = {
      url: userModuleURL.deleteCName,
      body: deleteProps,
      successCallback: deleteCNameSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const DomainHelperText = () => (
    <Typography component="span" variant="body2" fontWeight="bold">
      {translate('common.example')}: app.brand2social.com
    </Typography>
  );

  const handleCopyTxt = async () => {
    const txtRecord = `b2s-${domainUrl} ${cNameKey}`;
    try {
      await navigator.clipboard.writeText(txtRecord);
      setCopiedTxt(true);
      setTimeout(() => setCopiedTxt(false), 2000);
    } catch (err) {
      showToast('Failed to copy TXT record', 'e');
    }
  };

  const handleCopyCname = async () => {
    const cnameRecord = `${domainUrl} app.brand2social.com`;
    try {
      await navigator.clipboard.writeText(cnameRecord);
      setCopiedCname(true);
      setTimeout(() => setCopiedCname(false), 2000);
    } catch (err) {
      showToast('Failed to copy CNAME record', 'e');
    }
  };

  const handleCopyCnameValue = async () => {
    try {
      await navigator.clipboard.writeText('app.brand2social.com');
      setCopiedCnameValue(true);
      setTimeout(() => setCopiedCnameValue(false), 2000);
    } catch (err) {
      showToast('Failed to copy CNAME value', 'e');
    }
  };

  const handleCopyTxtValue = async () => {
    try {
      await navigator.clipboard.writeText(cNameKey);
      setCopiedTxtValue(true);
      setTimeout(() => setCopiedTxtValue(false), 2000);
    } catch (err) {
      showToast('Failed to copy TXT value', 'e');
    }
  };

  return (
    <>
      <Helmet>
        <title>{translate('trail_plan.white_label')}</title>
      </Helmet>
      {domainDetails && (
        <>
          <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
            <Card
              sx={{
                minHeight: 'calc(100vh - 160px)',
                maxHeight: 'calc(100vh - 160px)',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
              }}
            >
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between" 
                sx={{ mb: 3 }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {translate('manage_branding.white_label.domain_mapping', 'Domain Mapping')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('manage_branding.white_label.connect_custom_domain', 'Connect your custom domain to Brand2Social')}
                  </Typography>
                </Box>
                {(!isCustomDomain || isPortalAdmin) && (
                  <Button
                    variant="contained"
                    startIcon={<YouTubeIcon sx={{ color: '#f86767ff' }} />}
                    onClick={() => window.open('https://www.youtube.com/watch?v=amqIich3-68&ab_channel=Brand2Social', '_blank')}
                  >
                    {translate('manage_branding.white_label.tutorial', 'Tutorial')}
                  </Button>
                )}
              </Stack>
              <Box sx={{ mb: 3 }}>
                <Stack direction="column" alignItems="center" spacing={2}>
                  <TextField
                    value={domainUrl}
                    label={translate('manage_branding.white_label.domain_url')}
                    variant="outlined"
                    disabled={isDomainVerified}
                    onChange={handleInputChange}
                    error={Boolean(formValidationErrors?.domainUrl)}
                    fullWidth
                    sx={{ maxWidth: 400 }}
                    helperText={
                      showHelperText && !domainUrl ? (
                        <DomainHelperText />
                      ) : (
                        formValidationErrors?.domainUrl
                      )
                    }
                  />

                  {isDomainVerified && domainUrl && (
                    <Button
                      href={domainUrl.startsWith('http') ? domainUrl : `https://${domainUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      color="success"
                    >
                      {translate('manage_branding.white_label.visit_domain', 'Visit Domain')}
                    </Button>
                  )}
                </Stack>
              </Box>


              {!isDomainVerified && (
                <>
                  {!cNameKey ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        loading={isNextLoading}
                      >
                        {translate('common.submit')}
                      </LoadingButton>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
                        {translate('manage_branding.white_label.add_dns_records', 'Add DNS Records')}
                      </Typography>
                      
                      {/* CNAME Record */}
                      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {translate('manage_branding.white_label.cname_record', 'CNAME Record')}
                          </Typography>
                          <Button
                            href={`https://mxtoolbox.com/SuperTool.aspx?action=cname:${domainUrl}`}
                            target="_blank"
                            variant="outlined"
                            size="small"
                          >
                            {translate('manage_branding.white_label.verify', 'Verify')}
                          </Button>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">{translate('manage_branding.white_label.name', 'Name')}</Typography>
                            <Typography variant="body2">{domainUrl}</Typography>
                          </Box>
                          <Typography>→</Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">{translate('manage_branding.white_label.value', 'Value')}</Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2">app.brand2social.com</Typography>
                              <Tooltip title={copiedCnameValue ? translate('manage_branding.white_label.copied', 'Copied!') : translate('manage_branding.white_label.copy', 'Copy')}>
                                <IconButton
                                  size="small"
                                  onClick={handleCopyCnameValue}
                                  color={copiedCnameValue ? "success" : "primary"}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>

                      {/* TXT Record */}
                      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {translate('manage_branding.white_label.txt_record', 'TXT Record')}
                          </Typography>
                          <Button
                            href={`https://mxtoolbox.com/SuperTool.aspx?action=txt:b2s-${domainUrl}`}
                            target="_blank"
                            variant="outlined"
                            size="small"
                          >
                            {translate('manage_branding.white_label.verify', 'Verify')}
                          </Button>
                        </Stack>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">{translate('manage_branding.white_label.name', 'Name')}</Typography>
                            <Typography variant="body2">b2s-{domainUrl}</Typography>
                          </Box>
                          <Typography>→</Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" color="text.secondary">{translate('manage_branding.white_label.value', 'Value')}</Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{cNameKey}</Typography>
                              <Tooltip title={copiedTxtValue ? translate('manage_branding.white_label.copied', 'Copied!') : translate('manage_branding.white_label.copy', 'Copy')}>
                                <IconButton
                                  size="small"
                                  onClick={handleCopyTxtValue}
                                  color={copiedTxtValue ? "success" : "primary"}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>

                      {/* Info */}
                      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                        <InfoIcon color="info" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        <Typography variant="body2" sx={{ display: 'inline' }}>
                          {translate('manage_branding.white_label.dns_changes_take_24_hours')}
                        </Typography>
                      </Box>

                      {/* Verify Button */}
                      <Box sx={{ textAlign: 'center' }}>
                        <LoadingButton
                          variant="contained"
                          color="success"
                          loading={isVerifyLoading}
                          onClick={handleVerify}
                        >
                          {translate('common.verify')}
                        </LoadingButton>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {showDelete && (
                <Box sx={{ mt: '1rem', pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {translate('manage_branding.white_label.remove_domain_configuration', 'This will remove your domain configuration.')}
                  </Typography>
                  <LoadingButton 
                    variant="contained" 
                    color="error" 
                    loading={false} 
                    onClick={handleDeleteDomain}
                  >
                    {translate('manage_branding.white_label.delete_domain')}
                  </LoadingButton>
                </Box>
              )}
            </Card>
          </FormProvider>
        </>
      )}
    </>
  );
};

export default WhiteLabel;
